/**
 * What the scene holds: following the core's graph (new nodes get a place, removed ones go),
 * the filters, where new nodes appear, and the positions saved with the project.
 *
 * Positions saved in the project are always the 2D ones: in 3D they are kept aside
 * (`SceneMemory.flatLayout`, see `dimension.ts`) and these functions read and write there.
 */
import type { Graph } from "@protocol/Graph";
import type { Node } from "@protocol/Node";
import { buildGroups } from "../groups";
import { computeBundles } from "./bundlesOnScreen";
import { categoriesOf, shownWith } from "../../shared/categories";
import { linkKindShown } from "../../shared/kinds";
import { clusterOf } from "./forces";
import { seedPositions } from "./seed";
import { nodeRadius } from "./geometry";
import { pictureOf } from "../../shared/assets";
import type { Scene } from "./index";
import type { SceneFilters, SceneMemory, SimNode } from "./types";
import { centreOf, foldsOf, memberPlace, recentre, standInId, standInNode, standInRadius, type Fold } from "./folds";
import { CENTRE, HINT_LIFETIME_MS, SEED_FROM } from "./constants";

/** Filling the scene from the graph, and its saved positions. */
export interface SceneContent {
  /** Updates the scene from the graph (new nodes get a position, removed ones go). */
  sync(graph: Graph, filters: SceneFilters): { added: number };
  /** Recomputes visibility after a filter change. */
  applyFilters(filters: SceneFilters): void;
  /** Asks that a node created soon with this name appears at this world point. */
  hint(name: string, x: number, y: number, z?: number): void;
  /** Lets go of nodes the user placed, once the layout has settled around them. */
  releasePlaced(): void;
  /** Every node's position, rounded: node id → `[x, y]`. */
  positions(): Record<string, [number, number]>;
  /**
   * Moves the nodes listed in `positions` there and stops the layout. Returns how many nodes
   * were placed (nodes not listed keep their place).
   */
  applyPositions(positions: Record<string, [number, number]>): number;
}

/**
 * Puts a stand-in in `next` for every outermost folded group of `graph` (kept from `previous`
 * when it was already there) and marks the nodes it hides; a group unfolded since puts its
 * nodes back where its stand-in was. Returns how many stand-ins came or went (they count as
 * nodes added: the layout makes room).
 */
function fold(graph: Graph, previous: Map<string, SimNode>, next: Map<string, SimNode>): number {
  const folds = foldsOf(graph.groups ?? []);
  const groups = new Map<string, { fold: Fold; members: SimNode[] }>();
  for (const n of next.values()) {
    const outer = n.node.group ? folds.get(n.node.group) : undefined;
    if (!outer) continue;
    const entry = groups.get(outer.id) ?? { fold: { group: outer, members: [], offsets: new Map() }, members: [] as SimNode[] };
    entry.members.push(n);
    groups.set(outer.id, entry);
  }
  // Folded groups with no node yet still show as a disc.
  for (const outer of new Set(folds.values())) if (!groups.has(outer.id)) groups.set(outer.id, { fold: { group: outer, members: [], offsets: new Map() }, members: [] });
  let changed = 0;
  for (const [id, { fold, members }] of groups) {
    const key = standInId(id);
    const before = previous.get(key);
    const node = standInNode(fold.group, members);
    fold.members = members;
    if (before?.fold) {
      // Kept: the members it already held keep their offsets, newcomers start beside it.
      for (const m of members) fold.offsets.set(m.id, before.fold.offsets.get(m.id) ?? [m.x - before.x, m.y - before.y]);
      Object.assign(before, { node, fold });
      next.set(key, before);
    } else {
      const [x, y] = members.length ? centreOf(members) : [CENTRE[0], CENTRE[1]];
      const sim: SimNode = { id: key, node, x, y, z: 0, vx: 0, vy: 0, vz: 0, fixed: false, placed: false, r: 5, degree: 0, visible: true, fold };
      recentre(sim, fold);
      next.set(key, sim);
      changed++;
    }
    for (const m of members) m.foldedIn = next.get(key)!;
  }
  // Groups unfolded (or gone): their nodes come back where the disc was.
  for (const [key, before] of previous) {
    if (!before.fold || next.has(key)) continue;
    changed++;
    for (const m of before.fold.members) {
      const n = next.get(m.id);
      if (!n || n.foldedIn) continue;
      const [x, y] = memberPlace(before, before.fold, m.id);
      Object.assign(n, { x, y, vx: 0, vy: 0, goal: null });
    }
  }
  return changed;
}

/** The content of `scene`. */
export function content(scene: Scene, memory: SceneMemory): SceneContent {
  /** Where a node that just appeared starts: a fresh hint, or near its category's corner. */
  const startPosition = (node: Node): { x: number; y: number; z: number; placed: boolean } => {
    const hint = memory.hints.get(node.name);
    if (hint && Date.now() - hint.at < HINT_LIFETIME_MS) {
      memory.hints.delete(node.name);
      return { x: hint.x, y: hint.y, z: scene.dimension === "3d" ? hint.z : 0, placed: true };
    }
    const [cx, cy] = clusterOf(scene.categories, node.categories.slice(0, 1)).map((c) => c * scene.spacing) as [number, number];
    const around = 160 * scene.spacing;
    const z = scene.dimension === "3d" ? (Math.random() - 0.5) * around : 0;
    return { x: cx + (Math.random() - 0.5) * around, y: cy + (Math.random() - 0.5) * around, z, placed: false };
  };

  return {
    sync(graph, filters) {
      scene.categories = categoriesOf(graph);
      const previous = scene.byId;
      const next = new Map<string, SimNode>();
      let added = 0;
      /** The new nodes nobody placed (no hint): many of them are seeded together. */
      const newcomers: SimNode[] = [];
      for (const node of graph.nodes) {
        const existing = previous.get(node.id);
        if (existing) {
          existing.node = node;
          existing.foldedIn = null;
          next.set(node.id, existing);
          continue;
        }
        added++;
        const start = startPosition(node);
        const sim: SimNode = { id: node.id, node, x: start.x, y: start.y, z: start.z, vx: 0, vy: 0, vz: 0, fixed: start.placed, placed: start.placed, r: 5, degree: 0, visible: true };
        next.set(node.id, sim);
        if (!start.placed) newcomers.push(sim);
      }
      // Many at once (a project opened, a big scan): in discs by group, near where they will settle.
      if (newcomers.length >= SEED_FROM && scene.dimension === "2d") seedPositions(newcomers, [CENTRE[0] * scene.spacing, CENTRE[1] * scene.spacing], scene.spacing);
      added += fold(graph, previous, next);
      scene.byId = next;
      scene.nodes = [...next.values()];
      /** Where a link's end is drawn: the node, or the stand-in of the folded group hiding it. */
      const end = (id: string) => {
        const n = next.get(id);
        return n?.foldedIn ?? n;
      };
      /** Links already drawn between two ends, so the links of a folded group are drawn once per kind. */
      const drawn = new Set<string>();
      scene.links = graph.links.flatMap((link) => {
        const a = end(link.from);
        const b = end(link.to);
        if (!a || !b || a === b) return [];
        if (a.fold || b.fold) {
          const key = `${a.id}\n${b.id}\n${link.kind}`;
          if (drawn.has(key)) return [];
          drawn.add(key);
        }
        return [{ link, a, b, visible: true }];
      });
      for (const n of scene.nodes) n.degree = 0;
      for (const l of scene.links) {
        l.a.degree++;
        l.b.degree++;
      }
      for (const n of scene.nodes) n.r = n.fold ? standInRadius(n.fold.members.length) : nodeRadius(n.degree, !!pictureOf(n.node.attachments));
      scene.groups = buildGroups(graph.groups ?? [], scene.nodes.map((n) => ({ sim: n, group: n.node.group ?? null })));
      scene.groupById = new Map(scene.groups.map((g) => [g.id, g]));
      scene.applyFilters(filters);
      return { added };
    },

    applyFilters(filters) {
      for (const n of scene.nodes) n.visible = !n.foldedIn && !n.fold && shownWith(n.node.categories, filters.hiddenCategories);
      for (const n of scene.nodes) if (n.fold) n.visible = n.fold.members.some((m) => shownWith(m.node.categories, filters.hiddenCategories)) || !n.fold.members.length;
      for (const l of scene.links) l.visible = linkKindShown(filters.linkKinds, l.link.kind) && l.a.visible && l.b.visible;
      scene.showGroups = filters.groups;
      scene.layoutGroups();
      computeBundles(scene, filters.groups && filters.bundles);
      scene.relink();
    },

    hint(name, x, y, z = 0) {
      memory.hints.set(name, { x, y, z, at: Date.now() });
    },

    releasePlaced() {
      for (const n of scene.nodes) {
        if (!n.placed) continue;
        n.placed = false;
        n.fixed = false;
        n.vx = 0;
        n.vy = 0;
      }
    },

    positions() {
      const positions: Record<string, [number, number]> = {};
      for (const n of scene.nodes) {
        // In 3D, the saved positions stay the 2D ones (a node added in 3D has only its 3D place).
        // A node still gliding to the shared layout is already counted where it goes.
        // A stand-in is not a node of the graph: its members are saved where it carries them.
        if (n.fold) continue;
        const folded = n.foldedIn?.fold && n.foldedIn;
        const [x, y] = memory.flatLayout?.get(n.id) ?? (folded ? memberPlace(folded, folded.fold!, n.id) : n.goal ? [n.goal.x, n.goal.y] : [n.x, n.y]);
        // A node whose place is broken is left out rather than sent (it would arrive as `null`).
        if (Number.isFinite(x) && Number.isFinite(y)) positions[n.id] = [Math.round(x), Math.round(y)];
      }
      return positions;
    },

    applyPositions(positions) {
      let placed = 0;
      for (const n of scene.nodes) {
        const p = positions[n.id];
        if (!p || !Number.isFinite(p[0]) || !Number.isFinite(p[1])) continue;
        Object.assign(n, { x: p[0], y: p[1], vx: 0, vy: 0, vz: 0, fixed: false, placed: false, goal: null });
        memory.flatLayout?.set(n.id, [p[0], p[1]]);
        placed++;
      }
      for (const n of scene.nodes) if (n.fold) recentre(n, n.fold);
      // In 3D, the 2D positions are only a start: the layout spreads them in depth again.
      if (placed) scene.alpha = scene.dimension === "3d" ? 1 : 0;
      return placed;
    },
  };
}
